import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CartelAI, NewsletterAI } from "../../types/types";
import { useGlobalState } from "../../context/useGlobalState";
import { getDrafts, deleteDraftItem, updateDraftItem, createEmptyProject } from "../../api";
import Icon from "../../components/common/Icon";
import Dropdown from "../../components/common/Dropdown";
import TextModal from "../../components/modals/ProjectTextModal";
import DeleteModal from "../../components/modals/DeleteModal";
import AddCartelToProjectModal from "../../components/modals/AddCartelToProjectModal";
import AddNewsletterToProjectModal from "../../components/modals/AddNewsletterToProjectModal";
import Pagination from "../../components/common/Pagination";
import s from "../../components/common/ContentSurface.module.css";

type TextDraftType = "cartel" | "newsletter";
type TextDraftItem = CartelAI | NewsletterAI;

interface TextDraftsProps {
    itemType: TextDraftType;
}

const PAGE_SIZE = 100;

const TextDrafts: React.FC<TextDraftsProps> = ({ itemType }) => {
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const { data } = useGlobalState();
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [drafts, setDrafts] = useState<TextDraftItem[]>([]);
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [search, setSearch] = useState<string>("");
    const [debouncedSearch, setDebouncedSearch] = useState<string>("");
    const [selectedItem, setSelectedItem] = useState<TextDraftItem | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
    // Draft currently being assigned to a project (opens the project picker modal).
    const [addTargetId, setAddTargetId] = useState<string | null>(null);

    const editBasePath = `/create/${itemType}-drafts`;
    const titleKey = itemType === "cartel" ? "draftsCartelTitle" : "draftsNewsletterTitle";
    const subtitleKey = itemType === "cartel" ? "draftsCartelSubtitle" : "draftsNewsletterSubtitle";

    // Debounce the search box.
    useEffect(() => {
        const id = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 350);
        return () => clearTimeout(id);
    }, [search]);

    const fetchDrafts = async () => {
        if (!token) return;
        try {
            const response = await getDrafts({
                token,
                itemType,
                page,
                limit: PAGE_SIZE,
                language: selectedContentLanguage,
                query: debouncedSearch || undefined,
            });
            setDrafts((response.items as TextDraftItem[]) || []);
            setTotalPages(response.totalPages || 1);
            setTotalCount(response.totalCount || 0);
        } catch (error) {
            console.error(`Error fetching ${itemType} drafts:`, error);
        }
    };

    // Language + search applied server-side so pagination/total stay consistent.
    useEffect(() => {
        fetchDrafts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, itemType, selectedContentLanguage, debouncedSearch]);

    const handleDeleteConfirm = async () => {
        if (!token || !deleteTargetId) return;
        try {
            await deleteDraftItem({ token, itemId: deleteTargetId, itemType });
            setDeleteTargetId(null);
            if (drafts.length === 1 && page > 1) setPage((p) => p - 1);
            else await fetchDrafts();
        } catch (error) {
            console.error("Failed to delete draft:", error);
            setDeleteTargetId(null);
        }
    };

    // Assign a draft to a project (existing or newly created). The draft then leaves
    // the brouillons list (it now has a projectAIId).
    const handleAddToProject = async (projectId: string, newProjectName: string) => {
        if (!token || !addTargetId) return;
        try {
            let targetProjectAIId = projectId;
            if (projectId === "new") {
                if (!newProjectName.trim()) return;
                const created = await createEmptyProject({ token, name: newProjectName.trim() });
                if (!created?.id) return;
                targetProjectAIId = created.id;
            }
            await updateDraftItem({ token, itemId: addTargetId, itemType, targetProjectAIId });
            setAddTargetId(null);
            if (drafts.length === 1 && page > 1) setPage((p) => p - 1);
            else await fetchDrafts();
        } catch (error) {
            console.error("Failed to add draft to project:", error);
            setAddTargetId(null);
        }
    };

    return (
        <div className={s.page}>
            <div className={s.header}>
                <div className={s.headerLeft}>
                    <div>
                        <h1 className={s.title}>
                            {t(titleKey)} ({totalCount})
                        </h1>
                        <p className={s.subtitle}>{t(subtitleKey)}</p>
                    </div>
                </div>
            </div>

            <div className={s.divider} />

            <div className={s.toolbar}>
                <div className={s.search}>
                    <Icon name="ic-search" className={s.searchIcon} />
                    <input
                        className={s.searchInput}
                        type="text"
                        placeholder={t("searchByTitleVoiceLanguage")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div style={{ minWidth: 180 }}>
                    <Dropdown
                        label=""
                        value={selectedContentLanguage}
                        options={data?.languagesAI?.map((l: { code: string; label: string }) => ({
                            value: l.code,
                            label: l.label,
                        }))}
                        onChange={(language: string | null) => {
                            setSelectedContentLanguage(language);
                            setPage(1);
                        }}
                        placeholder={t("selectALanguage")}
                        loading={false}
                        error={false}
                    />
                </div>
            </div>

            <div className={s.list}>
                {drafts.map((item) => {
                    const langCode = item.language || "";
                    return (
                        <div key={item.id} className={s.row}>
                            <div className={s.rowLeft}>
                                <div className={s.fileThumb}>
                                    <Icon name="ic-file-text" className={s.playIcon} />
                                </div>
                                <div className={s.info}>
                                    <div className={s.nameRow}>
                                        <h4
                                            className={s.name}
                                            onClick={() =>
                                                navigate(`${editBasePath}/update-item/${item.id}`, { state: { item } })
                                            }
                                        >
                                            {item.name}
                                        </h4>
                                        <Icon
                                            name="ic-pencil"
                                            className={s.editIcon}
                                            onClick={() =>
                                                navigate(`${editBasePath}/update-item/${item.id}`, { state: { item } })
                                            }
                                        />
                                    </div>
                                    <p className={s.meta}>
                                        {item.text
                                            ? item.text.length > 100
                                                ? `${item.text.substring(0, 100)}...`
                                                : item.text
                                            : t("noInformationAvailable")}
                                    </p>
                                </div>
                            </div>

                            <div className={s.rowRight}>
                                {langCode && <span className={s.lang}>{langCode.toUpperCase()}</span>}
                                {item.text && (
                                    <Icon
                                        name="ic-file-text"
                                        className={s.iconAction}
                                        onClick={() => {
                                            setSelectedItem(item);
                                            setIsModalOpen(true);
                                        }}
                                    />
                                )}
                                <Icon
                                    name="ic-folder"
                                    className={s.iconAction}
                                    onClick={() => setAddTargetId(item.id)}
                                />
                                <button
                                    type="button"
                                    className={s.removeBtn}
                                    onClick={() => setDeleteTargetId(item.id)}
                                >
                                    {t("remove")}
                                </button>
                            </div>
                        </div>
                    );
                })}
                {drafts.length === 0 && <div className={s.emptyState}>{t("noInformationAvailable")}</div>}
            </div>

            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

            {isModalOpen && selectedItem && (
                <TextModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={selectedItem.name}
                    content={selectedItem.text || ""}
                />
            )}

            <DeleteModal
                isOpen={deleteTargetId !== null}
                onClose={() => setDeleteTargetId(null)}
                onConfirm={handleDeleteConfirm}
                message={t("deleteDraftConfirmation")}
                title={t("deleteDraft")}
            />

            {itemType === "cartel" ? (
                <AddCartelToProjectModal
                    isOpen={addTargetId !== null}
                    onClose={() => setAddTargetId(null)}
                    onAddToProject={handleAddToProject}
                    placeholder={t("addToProject")}
                />
            ) : (
                <AddNewsletterToProjectModal
                    isOpen={addTargetId !== null}
                    onClose={() => setAddTargetId(null)}
                    onAddToProject={handleAddToProject}
                    placeholder={t("addToProject")}
                />
            )}
        </div>
    );
};

export default TextDrafts;
